import { FC } from 'react';
import { X } from 'lucide-react';

import {
	PunchupTableContainer,
	PunchupHeaderRow,
	PunchupDataRow,
	PunchupColumnHeader,
} from 'features/common/components/Punchup/primitives/table/PunchupTable';
import { PunchupStepper } from 'features/common/components/Punchup/primitives/forms/fields/PunchupStepper';
import { EditableRow } from 'features/PunchupPages/CompedTickets/SendCompedTicketsModal';
import { TicketTypeSummary } from 'features/PunchupPages/CompedTickets/SendCompedTicketsModal/types';

import { SendPoolBudget, poolClampedMax, poolItemsFromBulkRows } from 'features/PunchupPages/SendTickets/poolBudget';

import { BulkTicketTypeSelect } from './BulkTicketTypeSelect';
import { COLUMN_HEADERS, GRID_COLUMNS } from './constants';
import styles from './BulkUploadTable.module.css';

export interface BulkUploadTableProps {
	rows: EditableRow[];
	ticketTypes: TicketTypeSummary[];
	/** Ticket-type ids whose aggregate requested quantity exceeds the type's own availability. */
	exceededTypeIds: Set<number>;
	/**
	 * Shared-pool budget — when present, a pooled tier's row stepper is hard-capped so the quantity requested
	 * across the pool's tiers (this row + every other row drawing from the same pool) can never exceed the
	 * pool's remaining capacity. Parity with the single-recipient stepper. Omitted when the event has no pools.
	 */
	poolBudget?: SendPoolBudget;
	/** Pool ids over their sellable capacity — a member row's stepper turns red even when its own type fits. */
	exceededPoolIds?: Set<number>;
	onUpdate: (id: string, update: Partial<Omit<EditableRow, 'id'>>) => void;
	onDeleteRow: (id: string) => void;
}

export const BulkUploadTable: FC<BulkUploadTableProps> = ({
	rows,
	ticketTypes,
	exceededTypeIds,
	poolBudget,
	exceededPoolIds,
	onUpdate,
	onDeleteRow,
}) => {
	// Each row tagged with its pool (by id), index-aligned with `rows`, so the shared clamp can subtract the
	// quantity every OTHER same-pool row already draws. Same helper as the single-recipient steppers.
	const poolRequests = poolBudget ? poolItemsFromBulkRows(rows, poolBudget) : [];
	// Per-row stepper ceiling = min(tier own-remaining, pool remaining − other same-pool rows' draw); a
	// non-pooled tier (or pool-less event) keeps just its own remaining.
	const getMaxQty = (row: EditableRow, index: number): number | undefined => {
		if (row.ticketTypeId === null) return undefined;
		const tt = ticketTypes.find((t) => t.iD === row.ticketTypeId);
		if (!tt) return undefined;
		const poolId = poolBudget?.poolIdByTypeId[row.ticketTypeId];
		return poolClampedMax(tt.amount - tt.sold, poolId, poolRequests, index, poolBudget);
	};

	return (
		<PunchupTableContainer columns={GRID_COLUMNS}>
			<thead>
				<PunchupHeaderRow>
					{COLUMN_HEADERS.map((h, i) => (
						<PunchupColumnHeader key={i}>{h}</PunchupColumnHeader>
					))}
				</PunchupHeaderRow>
			</thead>
			<tbody>
				{rows.map((row, rowIndex) => {
					const isEmailInvalid = row.email.trim() === '';
					const isTypeUnset = row.ticketTypeId === null;
					const isQtyZero = row.quantity === 0;
					// A row over-reaches when the aggregate across all rows exceeds EITHER the ticket type's own
					// availability OR the shared pool it belongs to — no single row looks wrong in isolation, so
					// the red goes on the stepper (where the manager reduces numbers), not the type selector. One
					// combined flag so the two constraints signal identically (the pool case was previously silent).
					const rowPoolId = row.ticketTypeId !== null ? poolBudget?.poolIdByTypeId[row.ticketTypeId] : undefined;
					const isExceeded =
						(row.ticketTypeId !== null && exceededTypeIds.has(row.ticketTypeId)) ||
						(rowPoolId !== undefined && (exceededPoolIds?.has(rowPoolId) ?? false));

					return (
						<PunchupDataRow key={row.id}>
							<td className={styles.cell}>
								<input
									className={`${styles.cellInput}${isEmailInvalid ? ` ${styles.invalid}` : ''}`}
									type="email"
									value={row.email}
									placeholder="email@example.com"
									onChange={(e) => onUpdate(row.id, { email: e.target.value })}
								/>
							</td>
							<td className={styles.cell}>
								<input
									className={styles.cellInput}
									type="text"
									value={row.firstName}
									placeholder="First name"
									onChange={(e) => onUpdate(row.id, { firstName: e.target.value })}
								/>
							</td>
							<td className={styles.cell}>
								<input
									className={styles.cellInput}
									type="text"
									value={row.lastName}
									placeholder="Last name"
									onChange={(e) => onUpdate(row.id, { lastName: e.target.value })}
								/>
							</td>
							<td className={styles.cell}>
								<BulkTicketTypeSelect
									ticketTypes={ticketTypes}
									value={row.ticketTypeId}
									onChange={(id) => onUpdate(row.id, { ticketTypeId: id })}
									isInvalid={isTypeUnset}
								/>
							</td>
							<td className={styles.quantityCell}>
								{/* error=true when the aggregate quantity over-reaches the type's own availability OR its
								    shared pool — highlights the − button and value digit in danger red. */}
								<PunchupStepper
									value={row.quantity}
									min={0}
									max={getMaxQty(row, rowIndex)}
									onChange={(v) => onUpdate(row.id, { quantity: v })}
									disabled={isQtyZero && row.ticketTypeId === null}
									error={isExceeded}
								/>
							</td>
							<td className={styles.deleteCell}>
								<button
									type="button"
									className={styles.deleteRowBtn}
									aria-label="Remove row"
									onClick={() => onDeleteRow(row.id)}
								>
									<X size={14} />
								</button>
							</td>
						</PunchupDataRow>
					);
				})}
			</tbody>
		</PunchupTableContainer>
	);
};
